import { cloneTemplate, fillOpening, setCompanyLogo, text } from './dom'
import { type Workspace, type PreviewActions } from './workspace'

const stages: Record<string, string> = {
  interested: 'Saved',
  applied: 'Applied',
  interview: 'Interviewing',
  offer: 'Offer',
  closed: 'Closed',
  archived: 'Archived'
}

export function createBoard(root: HTMLElement, workspace: Workspace, actions: PreviewActions) {
  const page = root.querySelector<HTMLElement>('[data-marketing-page="board"]')!
  let stage = 'interested'
  let query = ''
  let mode = 'overview'
  let selected = workspace.jobs[0].id
  let agendaOnly = false
  const filtered = () =>
    workspace.jobs.filter(
      (job) =>
        (agendaOnly ? ['interview', 'offer'].includes(job.stage) : stage === 'all' || job.stage === stage) &&
        `${job.company} ${job.title}`.toLowerCase().includes(query.toLowerCase())
    )

  function render() {
    const list = filtered()
    page.querySelectorAll<HTMLElement>('[data-preview-board-stage-count]').forEach((count) => {
      count.textContent = String(
        workspace.jobs.filter(
          (job) => count.dataset.previewBoardStageCount === 'all' || job.stage === count.dataset.previewBoardStageCount
        ).length
      )
    })
    page
      .querySelectorAll<HTMLElement>('[data-preview-board-stage]')
      .forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.previewBoardStage === stage)))
    page
      .querySelectorAll<HTMLElement>('[data-preview-board-mode]')
      .forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.previewBoardMode === mode)))
    page.querySelector<HTMLElement>('[data-preview-board-overview]')!.hidden = mode !== 'overview'
    page.querySelector<HTMLElement>('[data-preview-board-focus]')!.hidden = mode !== 'focus'
    page.querySelector('[data-preview-board-summary]')!.textContent =
      `${list.length} ${stage === 'interested' ? 'saved jobs waiting for you to apply' : 'jobs'}`
    page.querySelector('[data-preview-board-total]')!.textContent =
      `${list.length} jobs shown · ${workspace.jobs.length} on your board`
    page.querySelector<HTMLElement>('[data-preview-board-empty]')!.hidden = list.length > 0
    page.querySelector('[data-preview-board-jobs]')!.replaceChildren(
      ...list.map((job) => {
        const row = cloneTemplate(root, 'board-row')
        fillOpening(root, row, job)
        text(row, 'saved', job.saved.toLowerCase())
        text(row, 'stage', stages[job.stage])
        return row
      })
    )
    if (!list.some((job) => job.id === selected)) selected = list[0]?.id ?? ''
    page.querySelector('[data-preview-board-focus-list]')!.replaceChildren(
      ...list.map((job) => {
        const row = cloneTemplate(root, 'company-button')
        row.removeAttribute('data-preview-select-company')
        row.dataset.previewFocusJob = job.id
        row.setAttribute('aria-pressed', String(job.id === selected))
        setCompanyLogo(root, row.querySelector<HTMLElement>('[data-preview-logo]')!, job.company)
        text(row, 'company', job.company)
        text(row, 'subtitle', job.title)
        text(row, 'count', '')
        return row
      })
    )
    const job = list.find((item) => item.id === selected)
    page.querySelector('[data-preview-board-focus-position]')!.textContent = job
      ? `${list.indexOf(job) + 1} of ${list.length} jobs`
      : 'All caught up'
    page.querySelector('[data-preview-board-focus-title]')!.textContent = job?.title ?? 'No jobs to review.'
    page.querySelector('[data-preview-board-focus-company]')!.textContent = job
      ? `${job.company} · ${stages[job.stage]}`
      : ''
    page.querySelector('[data-preview-board-focus-location]')!.textContent = job ? `${job.location} · ${job.pay}` : ''
    page.querySelector<HTMLTextAreaElement>('[data-preview-board-focus-notes]')!.value = job?.notes ?? ''
    page.querySelector<HTMLButtonElement>('[data-preview-board-focus-applied]')!.disabled =
      !job || job.stage !== 'interested'
    page.querySelector<HTMLButtonElement>('[data-preview-board-focus-details]')!.disabled = !job
  }

  page.addEventListener('click', (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>('button')
    if (!button) return
    if (button.dataset.previewBoardStage) {
      stage = button.dataset.previewBoardStage
      agendaOnly = false
      render()
    }
    if (button.dataset.previewBoardMode) {
      mode = button.dataset.previewBoardMode
      render()
    }
    if (button.dataset.previewFocusJob) {
      selected = button.dataset.previewFocusJob
      render()
    }
    if (button.hasAttribute('data-preview-board-agenda')) {
      mode = 'focus'
      agendaOnly = true
      render()
    }
    if (button.hasAttribute('data-preview-board-focus-next')) {
      const list = filtered()
      selected = list[(list.findIndex((job) => job.id === selected) + 1) % list.length]?.id ?? ''
      render()
    }
    const job = workspace.jobs.find((item) => item.id === selected)
    if (job && button.hasAttribute('data-preview-board-focus-applied')) {
      job.stage = 'applied'
      actions.changed()
      actions.report('Application recorded.')
    }
    if (job && button.hasAttribute('data-preview-board-focus-save-notes')) {
      job.notes = page.querySelector<HTMLTextAreaElement>('[data-preview-board-focus-notes]')!.value
      actions.changed()
      actions.report('Notes saved.')
    }
  })
  page.querySelector('[data-preview-board-query]')!.addEventListener('input', (event) => {
    query = (event.target as HTMLInputElement).value
    render()
  })
  return { render }
}
